import { cnpj, cpf } from "cpf-cnpj-validator";

import JidUtils from "./JidUtils";

/**
 * Class responsible for validating contact and Pix data.
 * 
 * @class ValidationUtils
 * @example
 * const isValidEmail = ValidationUtils.validateEmail("user@example.com");
 * console.log(isValidEmail); // true or false
 */
class ValidationUtils {
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * 
   * @param {string} email - The email address to validate.
   * @returns {boolean} True if email is valid, false otherwise.
   */
  static validateEmail(email) {
    return this.EMAIL_REGEX.test(email);
  }

  /**
   * 
   * @param {string} birthday - The birthday date in text to validate.
   * @returns {boolean} True if birthday date is before currentDatetime, false otherwise.
   */
  static validateBirthday(birthday) {
    return !(birthday && new Date(birthday) > new Date());
  }

  /**
   * 
   * @param {string} cpfValue - The CPF to validate.
   * @returns {boolean} True if CPF is valid, false otherwise.
   */
  static validateCpf(cpfValue) {
    return cpf.isValid(cpfValue);
  }

  /**
   * 
   * @param {string} cnpjValue - The CNPJ to validate.
   * @returns {boolean} True if CNPJ is valid, false otherwise.
   */
  static validateCnpj(cnpjValue) {
    return cnpj.isValid(cnpjValue);
  }

  /**
   * 
   * @param {string} cpfCnpj - The CPF/CNPJ to validate.
   * @param {boolean} isCpf - True if 'cpfCnpj' is CPF, false otherwise.
   * @returns {boolean} True if CPF/CNPJ is valid, false otherwise.
   */
  static validateCpfCnpj(cpfCnpj, isCpf) {
    return isCpf ? this.validateCpf(cpfCnpj) : this.validateCnpj(cpfCnpj);
  }

  /**
   * 
   * @param {string} phoneNumber - The phone number to validate.
   * @returns {boolean} True if phone number length is 10 or 11 characters, false otherwise.
   */
  static validatePixPhoneNumber(phoneNumber) {
    return [10, 11].includes(phoneNumber.length);
  }

  /**
   * 
   * @param {string} phoneNumber - The phone number to validate.
   * @param {string} lid - The lid to validate.
   * @returns {boolean} True if sanitized phone number OR sanitized lid are not 'NULL', false otherwise. The sanitization
   * consists of removing all characters that are not numbers.
   */
  static validatePhoneNumberLid(phoneNumber, lid) {
    const clearPhoneNumber = phoneNumber ? JidUtils.unformatAndClearJid(phoneNumber) : null;
    const clearLid = lid ? JidUtils.unformatAndClearJid(lid) : null;
    return !!(clearPhoneNumber || clearLid);
  }
}

export default ValidationUtils;
